import React, { useState, useEffect, useMemo } from "react";
import Quiz from "../../components/Quiz/Quiz.jsx";
import { FiSearch } from "react-icons/fi";
import { Link } from "react-router-dom";
import { formatEducLevel } from "../../assets/data/Helpers.js";
import "./Becas.css";

import scholarships from "../../assets/data/becas_actualizadas.json";

const categoryColors = {
  "General / Interdisciplinaria": "#7c3cff",
  "Artes y Diseño": "#4f9fd9",
  "Ciencias Sociales y Humanidades": "#d68a3f",
  "Educación y Pedagogía": "#e65c79",
  "Ingeniería, Ciencia y Tecnología": "#3b82f6",
  "Negocios, Economía y Finanzas": "#b8942e",
  "Salud y Medicina": "#c65bea",
};

export default function Becas() {
  const [started, setStarted] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [currentPage, setCurrentPage] = useState(1);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(scholarships.map((b) => b["Categoría"])));
    return ["Todos", ...cats];
  }, []);

  const filtered = scholarships.filter((b) => {
    const q = search.toLowerCase();

    const matchesText =
      b["Categoría"].toLowerCase().includes(q) ||
      b["Nombre de la Beca"].toLowerCase().includes(q) ||
      b["Institución Otorgante"].toLowerCase().includes(q);

    const matchesCategory =
      selectedCategory === "Todos" || b["Categoría"] === selectedCategory;

    return matchesText && matchesCategory;
  });

  const perPage = 12;
  const totalPages = Math.ceil(filtered.length / perPage);
  const startIdx = (currentPage - 1) * perPage;
  const pageItems = filtered.slice(startIdx, startIdx + perPage);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  function getPaginationRange(currentPage, totalPages) {
    const delta = 1;
    const range = [];
    const left = currentPage - delta;
    const right = currentPage + delta;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages) {
        range.push(i);
      } else if (i >= left && i <= right) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }

    return range;
  }

  return (
    <main className="becas-page">
      <section className="becas-hero">
        <div className="becas-hero__inner">
          {/* <span className="becas-pill">Becas ZY</span> */}

          <h1>Encuentra tu beca ideal</h1>

          <p>
            Responde unas breves preguntas y te recomendaremos las oportunidades
            de becas que más se ajusten a tu perfil.
          </p>

          <div className="becas-hero__meta">
            <span>Impulsado por IA</span>
            <span>Oportunidades para estudiantes en México</span>
          </div>
        </div>
      </section>

      <section className={`becas-quiz-section ${started ? "is-started" : ""}`}>
        <div
          className={`becas-quiz-card ${started ? "becas-quiz-card--active" : ""}`}
        >
          {!started ? (
            <>
              <div className="becas-quiz-card__content">
                <span className="becas-quiz-card__eyebrow">
                  Recomendador inteligente
                </span>

                <h2>Descubre opciones según tu perfil.</h2>

                <p>
                  Te hacemos unas preguntas rápidas y usamos tus respuestas para
                  acercarte a becas que podrían interesarte.
                </p>
              </div>

              <button
                type="button"
                className="quiz-button quiz-button--start"
                onClick={() => setStarted(true)}
              >
                ¡Comenzar Quiz!
              </button>
            </>
          ) : (
            <div className="becas-quiz-active">
              <Quiz />
            </div>
          )}
        </div>
      </section>

      <section className="all-becas">
        <div className="all-becas__heading">
          <span>Explorar</span>
          <h2>Explora todas las becas</h2>
          <p>
            Busca por categoría, nombre de beca o institución otorgante para
            encontrar oportunidades disponibles.
          </p>
        </div>

        <div className="becas-tools">
          <div className="becas-search-wrapper">
            <FiSearch className="search-icon" />
            <input
              className="becas-search"
              type="text"
              placeholder="Filtrar por categoría, beca o institución…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="category-filter">
            {categories.map((cat) => {
              const col =
                cat === "Todos"
                  ? "var(--becas-purple)"
                  : categoryColors[cat] || "var(--becas-purple)";

              const isActive = cat === selectedCategory;

              return (
                <button
                  key={cat}
                  type="button"
                  className={`category-pill ${
                    isActive ? "category-pill--active" : ""
                  }`}
                  style={{
                    "--category-color": col,
                  }}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setCurrentPage(1);
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        <ul className="becas-list">
          {pageItems.map((b) => {
            const borderColor = categoryColors[b["Categoría"]] || "#7c3cff";
            const originalIndex = scholarships.indexOf(b);

            return (
              <li
                key={`${b["Nombre de la Beca"]}-${originalIndex}`}
                className="beca-card"
              >
                <div className="beca-card__image-wrap">
                  <img
                    src={b["Image URL"]}
                    alt={b["Nombre de la Beca"]}
                    className="beca-card__img"
                  />
                </div>

                <div className="beca-card__body">
                  <span
                    className="beca-card__category"
                    style={{
                      "--category-color": borderColor,
                    }}
                  >
                    {b["Categoría"]}
                  </span>

                  <h3 className="beca-card__title">{b["Nombre de la Beca"]}</h3>

                  <div className="beca-card__info">
                    <p>
                      <strong>Institución:</strong> {b["Institución Otorgante"]}
                    </p>

                    <p>
                      <strong>Nivel Educativo:</strong>{" "}
                      {formatEducLevel(b["Nivel Educativo"])}
                    </p>
                  </div>

                  <Link
                    to={`/becas/${originalIndex}`}
                    className="beca-card__link"
                  >
                    Ver detalles
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              type="button"
              className="page-button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              aria-label="Página anterior"
            >
              ←
            </button>

            {getPaginationRange(currentPage, totalPages).map((item, idx) =>
              typeof item === "number" ? (
                <button
                  key={idx}
                  type="button"
                  className={`page-button page-button--number ${
                    item === currentPage ? "page-button--active" : ""
                  }`}
                  onClick={() => setCurrentPage(item)}
                >
                  {item}
                </button>
              ) : (
                <span key={idx} className="page-ellipsis">
                  {item}
                </span>
              ),
            )}

            <button
              type="button"
              className="page-button"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              aria-label="Página siguiente"
            >
              →
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
